import type { RuntimeLintContext } from './runtimeRules';

/**
 * Poses the composition and hands back the document to measure at that frame.
 *
 * Two shapes have to work. Against a sandboxed luminary the host's `seek` is async and returns a
 * script-disabled copy posed at that frame, because the composition itself is unreachable. Against an
 * older one — and against the hand-built contexts the rule specs use — `seek` poses the live document
 * in place and returns nothing, so the document to measure is the one already on the context.
 *
 * Null when the composition cannot be posed at all, which is the signal for a sampling rule to report
 * nothing rather than measure the same frame repeatedly and read the stillness as a finding.
 *
 * Posing is one-way: there is no restoring the frame the composition arrived at. A sandboxed host
 * hands back facts about its timeline rather than the timeline itself, so nothing here can seek it
 * back, and re-posing through `seek` would only move the copy to another arbitrary frame at the
 * price of a whole round trip. `RuntimeLintContext.document` says what that leaves a later rule
 * measuring.
 */
export type Poser = (progress: number) => Promise<Document>;

export function poserFor(context: RuntimeLintContext): Poser | null {
    const { seek, timeline, document } = context;
    if (seek) {
        return async (progress: number) => (await seek(progress)) ?? document;
    }
    const duration = typeof timeline?.duration === 'function' ? timeline.duration() : 0;
    if (!timeline || typeof timeline.seek !== 'function') {
        return null;
    }
    const seekTimeline = timeline.seek.bind(timeline);
    return async (progress: number) => {
        seekTimeline(progress * (duration || 1));
        return document;
    };
}
